
import { useState, useEffect } from "react";
import { MeasurementToolsProps } from "../types/measurements";
import { toast } from "@/hooks/use-toast";
import { useToolUsage } from "./use-tool-usage";
import { MeasurementService } from "@/services/measurement-service";
import { useUnifiedMeasurements } from "./use-unified-measurements";
import { debouncedRender } from "@/utils/canvas-rendering-utils";

export const useMeasurementTools = ({ canvas, activeTool, currentZoom = 100 }: MeasurementToolsProps) => {
  const [measurementScale, setMeasurementScale] = useState(1);
  const { markMeasurementUsed } = useToolUsage(canvas, activeTool);

  // Update scale when zoom changes
  useEffect(() => {
    if (!canvas) return;
    
    // Calculate scale factor based on zoom level
    const newScale = currentZoom / 100;
    setMeasurementScale(newScale);
    
    // Update all measurement labels on the canvas
    MeasurementService.updateAllMeasurements(canvas, newScale);
  }, [currentZoom, canvas]);

  // Callback for when a measurement is completed
  const handleMeasurementComplete = (tool: 'ruler' | 'angle') => {
    markMeasurementUsed(tool);
  };

  // Use the unified measurements hook for all measurement functionality
  const {
    measurePoints,
    handleCanvasClick,
    resetDistanceMeasurement,
    resetAngleMeasurement,
    updateMeasurementScale,
    clearMeasurements
  } = useUnifiedMeasurements({
    canvas,
    activeTool: activeTool === 'ruler' || activeTool === 'angle' ? activeTool : null,
    onToast: toast,
    scale: measurementScale,
    onMeasurementComplete: handleMeasurementComplete
  });

  // Update object interactivity based on active tool
  useEffect(() => {
    if (!canvas) return;

    const isMeasuring = activeTool === 'ruler' || activeTool === 'angle';
    const isErasing = activeTool === 'eraser';
    
    canvas.getObjects().forEach(obj => {
      if ((obj as any).data?.isTemporary || 
          (obj as any).data?.isPermanent || 
          (obj as any).data?.type === 'angleMeasurement') {
        return;
      }

      // When using eraser or measurement tools, objects shouldn't be selectable
      obj.selectable = !isMeasuring && !isErasing;
      obj.evented = !isMeasuring && !isErasing;
    });

    debouncedRender(canvas);
  }, [activeTool, canvas]);

  return {
    measurePoints,
    handleCanvasClick,
    resetDistanceMeasurement,
    resetAngleMeasurement,
    clearMeasurements,
    updateMeasurementScale,
    currentZoom,
  };
};
